import { useState } from "react";
import { useForm } from "react-hook-form";
import * as Sentry from "@sentry/react";
import Button from "@/components/ui/Button";
import ScriptText from "@/components/ui/ScriptText";
import { contactContent } from "@/data/content";
import { useContent } from "@/hooks/useContent";

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  howHeard: string;
  message: string;
  marketingOptIn: boolean;
  website: string; // honeypot
}

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const LIMITS = {
  name: 100,
  email: 254,
  phone: 30,
  howHeard: 200,
  message: 1000,
} as const;

function CharCount({ value, max }: { value: string | undefined; max: number }) {
  const len = value?.length ?? 0;
  const ratio = len / max;
  const color =
    ratio >= 1
      ? "text-faded-terracotta"
      : ratio >= 0.85
        ? "text-faded-terracotta/70"
        : "text-warm-stone/40";
  return (
    <p className={`mt-2 text-right font-body text-xs tabular-nums transition-colors duration-300 ${color}`}>
      {len} / {max}
    </p>
  );
}

export default function ContactForm() {
  const content = useContent("pages/contact", contactContent);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ContactFormData>();

  const [status, setStatus] = useState<SubmitStatus>("idle");

  const onSubmit = async (data: ContactFormData) => {
    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          howHeard: data.howHeard,
          message: data.message,
          marketingOptIn: data.marketingOptIn,
          website: data.website,
        }),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setStatus("success");
      } else {
        const err = new Error(result.error || "Contact form submission failed");
        Sentry.captureException(err, { tags: { form: "contact" } });
        setStatus("error");
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { form: "contact" } });
      setStatus("error");
    }
  };

  const labelClasses =
    "block mb-2 font-serif text-sm tracking-wider uppercase text-warm-stone";
  const inputClasses =
    "w-full border-0 border-b border-warm-stone/40 bg-transparent px-0 py-4 font-body text-lg text-soft-charcoal placeholder:text-warm-stone/60 focus:border-faded-terracotta focus:outline-none transition-colors duration-300";

  if (status === "success") {
    return (
      <div className="rounded-sm bg-cream/50 p-8 lg:p-12">
        <div className="py-12 text-center">
          <ScriptText className="mb-4 block text-3xl lg:text-4xl">
            {content.successScript}
          </ScriptText>
          <p className="mx-auto max-w-md font-body leading-relaxed text-soft-charcoal/80">
            {content.successBody}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-sm bg-cream/50 p-8 lg:p-12">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-10" noValidate>
        {/* Honeypot — visually hidden, bots fill it */}
        <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register("website")}
          />
        </div>

        {/* Name */}
        <div>
          <label htmlFor="name" className={labelClasses}>
            Name
          </label>
          <input
            id="name"
            type="text"
            placeholder="Your Name"
            maxLength={LIMITS.name}
            className={inputClasses}
            {...register("name", { required: "Please enter your name" })}
          />
          {errors.name && (
            <p className="mt-2 text-xs text-faded-terracotta">{errors.name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className={labelClasses}>
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="Email Address"
            className={inputClasses}
            maxLength={LIMITS.email}
            {...register("email", {
              required: "Please enter your email",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address",
              },
            })}
          />
          {errors.email && (
            <p className="mt-2 text-xs text-faded-terracotta">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className={labelClasses}>
            Phone
          </label>
          <input
            id="phone"
            type="tel"
            placeholder="Phone Number"
            maxLength={LIMITS.phone}
            className={inputClasses}
            {...register("phone")}
          />
        </div>

        {/* How Heard */}
        <div>
          <label htmlFor="howHeard" className={labelClasses}>
            How did you hear about us?
          </label>
          <input
            id="howHeard"
            type="text"
            placeholder="How did you hear about us?"
            maxLength={LIMITS.howHeard}
            className={inputClasses}
            {...register("howHeard")}
          />
          <CharCount value={watch("howHeard")} max={LIMITS.howHeard} />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className={labelClasses}>
            Message
          </label>
          <textarea
            id="message"
            rows={4}
            placeholder="We would love to hear your thoughts..."
            maxLength={LIMITS.message}
            className={`${inputClasses} resize-none`}
            {...register("message", { required: "Please include a message" })}
          />
          <div className="flex items-start justify-between">
            {errors.message ? (
              <p className="mt-2 text-xs text-faded-terracotta">{errors.message.message}</p>
            ) : (
              <span />
            )}
            <CharCount value={watch("message")} max={LIMITS.message} />
          </div>
        </div>

        {/* Marketing opt-in */}
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 appearance-none rounded-sm border border-warm-stone/40 bg-transparent checked:border-faded-terracotta checked:bg-faded-terracotta transition-colors duration-300 cursor-pointer"
            {...register("marketingOptIn")}
          />
          <span className="font-body text-sm leading-relaxed text-soft-charcoal/70">
            I'd like to hear about upcoming retreats and new experiences
            as they're announced.
          </span>
        </label>

        {status === "error" && (
          <p className="text-sm text-faded-terracotta">
            {content.errorText}
          </p>
        )}

        <Button type="submit" variant="filled" disabled={status === "submitting"}>
          {status === "submitting" ? content.submittingText : content.submitText}
        </Button>
      </form>
    </div>
  );
}
